import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';

import { sendHomePageTracking } from '@home-fai/helpers';

interface UseActiveTabProps {
    defaultTab?: FaiTabsEnum;
    tabConfig?: TabConfig;
}

export function useActiveTab({
    defaultTab = FaiTabsEnum.PURE_BYOU,
    tabConfig,
}: UseActiveTabProps): [FaiTabsEnum, (tab: FaiTabsEnum) => void] {
    const [activeTab, setActiveTab] = useState<FaiTabsEnum>(defaultTab);

    // Use refs to avoid stale closures in the hashchange handler
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;

    const tabConfigRef = useRef(tabConfig);
    tabConfigRef.current = tabConfig;

    const defaultTabRef = useRef(defaultTab);
    defaultTabRef.current = defaultTab;

    const handleHashChange = useCallback(() => {
        const hash = window.location.hash.replace('#', '');
        const normalizedHash = hash.toLowerCase();

        sendHomePageTracking(hash || defaultTabRef.current);

        if (normalizedHash === activeTabRef.current) {
            return;
        }

        if (tabConfigRef.current) {
            const selectedTab = tabConfigRef.current[normalizedHash as FaiTabsEnum];
            if (selectedTab) {
                setActiveTab(normalizedHash as FaiTabsEnum);
            }
        }
    }, []);

    useLayoutEffect(() => {
        handleHashChange();
    }, [handleHashChange]);

    useEffect(() => {
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [handleHashChange]);

    return [activeTab, setActiveTab];
}
